import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Briefcase, Crown, DollarSign, Ticket, Loader2, BarChart3, UserPlus, TrendingUp, PieChart as PieIcon, Percent, Download, Zap, Mail, HeartPulse } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, Area, AreaChart, PieChart, Pie, Cell } from "recharts";
import api, { apiErr } from "../lib/api";

const RANGES = [7, 14, 30, 60];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sampleCount, setSampleCount] = useState(null);
  const [sampleTarget, setSampleTarget] = useState(0);
  const [sampleBusy, setSampleBusy] = useState(false);

  const loadSample = async () => {
    try { const r = await api.get("/admin/sample-jobs"); setSampleCount(r.data.count); setSampleTarget(r.data.target); } catch (e) {}
  };
  useEffect(() => { loadSample(); }, []);

  const atTarget = sampleCount === sampleTarget;
  const seedSample = async () => {
    setSampleBusy(true);
    try {
      const r = await api.post("/admin/sample-jobs");
      toast.success(`Loaded ${r.data.count} sample jobs under ${r.data.employer}.`);
      await loadSample();
    } catch (e) { toast.error(apiErr(e)); } finally { setSampleBusy(false); }
  };
  const removeSample = async () => {
    setSampleBusy(true);
    try {
      const r = await api.delete("/admin/sample-jobs");
      toast.success(`Removed ${r.data.deleted} sample jobs.`);
      await loadSample();
    } catch (e) { toast.error(apiErr(e)); } finally { setSampleBusy(false); }
  };

  const sendDigest = async () => {
    if (!window.confirm("Send the weekly recap email to all active employers and admins now?")) return;
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/send-weekly-digest");
      toast.success(`Weekly digest sent — ${r.data.employers_sent} employer(s), ${r.data.admins_sent} admin(s).`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSendingDigest(false);
    }
  };

  const load = async (range = days) => {
    try {
      const [s, a, i] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/analytics?days=${range}`),
        api.get("/admin/insights"),
      ]);
      setStats(s.data);
      setAnalytics(a.data);
      setInsights(i.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    const t = setInterval(() => load(days), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading)
    return (
      <div className="max-w-7xl mx-auto px-5 py-24 text-center text-[#4B5563] font-mono2 text-sm flex items-center justify-center gap-2">
        <Loader2 size={18} className="animate-spin" /> Loading admin metrics…
      </div>
    );

  if (!stats) return null;

  const downloadCSV = (filename, rows) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportMetrics = () => {
    if (!analytics) return;
    const header = ["Date", "Signups", "Candidates", "Employers", "New Subscribers", "PAYG Revenue ($)", "Cumulative PAYG ($)"];
    const rows = analytics.series.map((b) => [b.date, b.signups, b.candidates, b.employers, b.subscribers, b.payg_revenue, b.cum_revenue]);
    rows.push([]);
    rows.push(["Totals", analytics.totals.signups, "", "", analytics.totals.subscribers, analytics.totals.payg_revenue, ""]);
    rows.push([]);
    rows.push(["Snapshot", new Date().toISOString()]);
    rows.push(["Est. MRR ($)", stats.estimated_mrr]);
    rows.push(["Active Subscribers", stats.active_subscribers]);
    rows.push(["Total Jobs", stats.total_jobs]);
    rows.push(["Total Applications", stats.total_applications]);
    rows.push(["Hire Rate (%)", stats.funnel?.hire_rate ?? 0]);
    downloadCSV(`darriker-metrics-${analytics.days}d-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...rows]);
    toast.success("Metrics CSV downloaded");
  };

  const cards = [
    { label: "Candidates", value: stats.candidates, sub: "registered", icon: Users, accent: "#002FA7" },
    { label: "Employers", value: stats.employers, sub: "registered", icon: Briefcase, accent: "#002FA7" },
    { label: "Candidate Premium", value: stats.candidate_premium_subscribers, sub: "active subs", icon: Crown, accent: "#7C3AED" },
    { label: "Employer Pro", value: stats.employer_pro_subscribers, sub: "active subs", icon: Crown, accent: "#7C3AED" },
    { label: "Jobs Posted", value: stats.total_jobs, sub: "total roles", icon: Briefcase, accent: "#0A0A0A" },
    { label: "Applications", value: stats.total_applications, sub: "submitted", icon: UserPlus, accent: "#0A0A0A" },
    { label: "Est. MRR", value: `$${Number(stats.estimated_mrr).toLocaleString()}`, sub: "monthly recurring", icon: DollarSign, accent: "#10B981" },
    { label: "Pay-as-you-go", value: `$${Number(stats.payg_revenue).toLocaleString()}`, sub: `${stats.payg_purchases} purchases`, icon: Ticket, accent: "#D97706" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 py-8" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-1">
        <BarChart3 size={26} className="text-[#002FA7]" />
        <h1 className="font-display font-black text-3xl tracking-tighter">Admin Console</h1>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <p className="text-sm text-[#4B5563]">Platform-wide signups, subscriptions & revenue · auto-refreshes every 30s</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-px border border-[#0A0A0A]" data-testid="range-selector">
            {RANGES.map((r) => (
              <button
                key={r}
                data-testid={`range-${r}`}
                onClick={() => setDays(r)}
                className={`font-mono2 text-xs uppercase px-3 py-1.5 transition-colors ${days === r ? "bg-[#0A0A0A] text-white" : "bg-white text-[#4B5563] hover:bg-[#F3F4F6]"}`}
              >
                {r}d
              </button>
            ))}
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={exportMetrics}
            className="font-mono2 text-xs uppercase px-3 py-1.5 border border-[#002FA7] text-[#002FA7] inline-flex items-center gap-1.5 hover:bg-[#002FA7] hover:text-white transition-colors"
          >
            <Download size={13} /> Export CSV
          </button>
          <button
            data-testid="send-digest-btn"
            onClick={sendDigest}
            disabled={sendingDigest}
            title="Weekly recaps also auto-send every Monday 08:00 UTC"
            className="font-mono2 text-xs uppercase px-3 py-1.5 bg-[#0A0A0A] text-white inline-flex items-center gap-1.5 hover:bg-[#002FA7] transition-colors disabled:opacity-50"
          >
            {sendingDigest ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Send weekly digest
          </button>
          <button
            data-testid="sample-jobs-btn"
            onClick={sampleCount > 0 && atTarget ? removeSample : seedSample}
            disabled={sampleBusy || sampleCount === null}
            title="Populate the public job board with fully-detailed demo roles under the Darriker Talent employer"
            className={`font-mono2 text-xs uppercase px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 border ${sampleCount > 0 && atTarget ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white" : "border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"}`}
          >
            {sampleBusy ? <Loader2 size={13} className="animate-spin" /> : <Briefcase size={13} />}
            {sampleCount === null ? "Sample jobs" : sampleCount === 0 ? `Load ${sampleTarget} sample jobs` : atTarget ? `Remove ${sampleCount} sample jobs` : `Update to ${sampleTarget} sample jobs`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#0A0A0A] border border-[#0A0A0A] mb-10" data-testid="admin-metrics">
        {cards.map((c) => (
          <div key={c.label} className="bg-white p-5" data-testid={`metric-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono2 text-xs uppercase text-[#4B5563]">{c.label}</span>
              <c.icon size={16} style={{ color: c.accent }} />
            </div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2" style={{ color: c.accent }}>{c.value}</div>
            <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF] mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {analytics && (
        <div className="grid lg:grid-cols-2 gap-6 mb-10" data-testid="admin-analytics">
          <div className="border border-[#0A0A0A] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><UserPlus size={16} className="text-[#002FA7]" /> Signups / day</h2>
              <span className="font-mono2 text-xs uppercase text-[#4B5563]">{analytics.totals.signups} in {analytics.days}d</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fontFamily: "monospace" }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: "monospace" }} />
                <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="candidates" stackId="a" fill="#002FA7" name="Candidates" />
                <Bar dataKey="employers" stackId="a" fill="#0A0A0A" name="Employers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-[#0A0A0A] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><TrendingUp size={16} className="text-[#10B981]" /> Revenue & subscribers</h2>
              <span className="font-mono2 text-xs uppercase text-[#10B981]">${Number(analytics.totals.payg_revenue).toLocaleString()} PAYG · {analytics.totals.subscribers} new subs</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={analytics.series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fontFamily: "monospace" }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: "monospace" }} />
                <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="payg_revenue" stroke="#10B981" strokeWidth={2} dot={false} name="PAYG $" />
                <Line type="monotone" dataKey="subscribers" stroke="#7C3AED" strokeWidth={2} dot={false} name="New subs" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {insights && (
        <>
          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            {/* Conversion rates */}
            <div className="border border-[#0A0A0A] p-5" data-testid="conversion-card">
              <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-5"><Percent size={16} className="text-[#10B981]" /> Free → Paid conversion</h2>
              <div className="space-y-5">
                {[
                  { label: "Candidate Premium", pct: insights.conversion.candidate_premium_pct, color: "#7C3AED" },
                  { label: "Employer Pro", pct: insights.conversion.employer_pro_pct, color: "#002FA7" },
                ].map((c) => (
                  <div key={c.label} data-testid={`conv-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span>{c.label}</span>
                      <span className="font-mono2 font-bold" style={{ color: c.color }}>{c.pct}%</span>
                    </div>
                    <div className="h-2.5 bg-[#F3F4F6]"><div className="h-2.5" style={{ width: `${Math.min(c.pct, 100)}%`, background: c.color }} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-[#E5E7EB] grid grid-cols-3 gap-4">
                <div>
                  <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">Premium MRR</div>
                  <div className="font-display font-black text-2xl text-[#7C3AED]">${Number(insights.revenue.candidate_premium_mrr).toLocaleString()}</div>
                </div>
                <div>
                  <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">Pro MRR</div>
                  <div className="font-display font-black text-2xl text-[#002FA7]">${Number(insights.revenue.employer_pro_mrr).toLocaleString()}</div>
                </div>
                <div>
                  <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">Flex MRR</div>
                  <div className="font-display font-black text-2xl text-[#10B981]">${Number(insights.revenue.flex_plan_mrr || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Flex/Remote marketplace supply */}
            {insights.flex_marketplace && (
              <div className="border border-[#0A0A0A] p-5" data-testid="flex-marketplace-card">
                <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Zap size={16} className="text-[#002FA7]" /> Flex &amp; Remote marketplace</h2>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Flex workers", value: insights.flex_marketplace.flex_workers, color: "#0A0A0A" },
                    { label: "Listed & searchable", value: insights.flex_marketplace.listed, color: "#10B981" },
                    { label: "On-site available", value: insights.flex_marketplace.onsite, color: "#002FA7" },
                    { label: "Remote available", value: insights.flex_marketplace.remote, color: "#7C3AED" },
                  ].map((s) => (
                    <div key={s.label} className="border border-[#E5E7EB] p-3">
                      <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">{s.label}</div>
                      <div className="font-display font-black text-2xl" style={{ color: s.color }}>{Number(s.value).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 font-mono2 text-xs text-[#4B5563]">{insights.flex_marketplace.flex_plan_subs} Flex-plan subscribers · ${Number((insights.flex_marketplace.flex_plan_subs || 0) * 99).toLocaleString()}/mo</div>
              </div>
            )}

            {/* Darriker Health marketplace supply */}
            {insights.health_marketplace && (
              <div className="border border-[#0A0A0A] p-5" data-testid="health-marketplace-card">
                <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><HeartPulse size={16} className="text-[#0E7C66]" /> Darriker Health marketplace</h2>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Healthcare workers", value: insights.health_marketplace.workers, color: "#0A0A0A" },
                    { label: "Listed & searchable", value: insights.health_marketplace.listed, color: "#0E7C66" },
                    { label: "Credential verified", value: insights.health_marketplace.credential_verified, color: "#10B981" },
                    { label: "Employer Pro (access)", value: insights.health_marketplace.employer_pro_subs, color: "#002FA7" },
                  ].map((s) => (
                    <div key={s.label} className="border border-[#E5E7EB] p-3">
                      <div className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">{s.label}</div>
                      <div className="font-display font-black text-2xl" style={{ color: s.color }}>{Number(s.value).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                {insights.health_marketplace.top_professions?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="health-top-professions">
                    {insights.health_marketplace.top_professions.map((p) => (
                      <span key={p.name} className="font-mono2 text-[10px] uppercase bg-[#0E7C66]/10 text-[#0E7C66] border border-[#0E7C66]/30 px-2 py-0.5">{p.name} · {p.count}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Plan distribution donut */}
            <div className="border border-[#0A0A0A] p-5" data-testid="plan-distribution-card">
              <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-2"><PieIcon size={16} className="text-[#002FA7]" /> Plan distribution</h2>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={insights.plan_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {insights.plan_distribution.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative PAYG revenue */}
            {analytics && (
              <div className="border border-[#0A0A0A] p-5" data-testid="cumulative-revenue-card">
                <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-2"><DollarSign size={16} className="text-[#10B981]" /> Cumulative PAYG</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={analytics.series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fontFamily: "monospace" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} />
                    <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                    <Area type="monotone" dataKey="cum_revenue" stroke="#10B981" strokeWidth={2} fill="url(#cumGrad)" name="Cumulative $" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Top jobs by applications */}
          <div className="border border-[#0A0A0A] p-5 mb-10" data-testid="top-jobs-card">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Briefcase size={16} className="text-[#002FA7]" /> Top jobs by applications</h2>
            {insights.top_jobs.length === 0 ? (
              <div className="text-sm text-[#9CA3AF] font-mono2">No applications yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(180, insights.top_jobs.length * 44)}>
                <BarChart layout="vertical" data={insights.top_jobs} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <YAxis type="category" dataKey="title" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} formatter={(v) => [`${v} applications`, ""]} />
                  <Bar dataKey="applications" fill="#002FA7" name="Applications" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}

      <div className="border border-[#0A0A0A] mb-10" data-testid="admin-funnel">
        <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Hiring funnel</h2>
          <span className="font-mono2 text-xs uppercase text-[#10B981]">{stats.funnel?.hire_rate ?? 0}% hire rate</span>
        </div>
        <div className="p-5 space-y-2">
          {[["new", "New"], ["shortlisted", "Shortlisted"], ["interview", "Interview"], ["hired", "Hired"], ["rejected", "Rejected"]].map(([k, label]) => {
            const v = (stats.funnel && stats.funnel[k]) || 0;
            const pct = stats.total_applications ? Math.round((v / stats.total_applications) * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-3" data-testid={`funnel-${k}`}>
                <span className="w-24 text-sm">{label}</span>
                <div className="flex-1 h-5 bg-[#F3F4F6]"><div className="h-5" style={{ width: `${pct}%`, background: k === "hired" ? "#10B981" : k === "rejected" ? "#FF3B30" : "#002FA7" }} /></div>
                <span className="font-mono2 text-sm w-8 text-right">{v}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-[#0A0A0A]">
        <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Recent signups</h2>
          <span className="font-mono2 text-xs uppercase text-[#4B5563]">{stats.total_users} total accounts</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-signups-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left font-mono2 text-xs uppercase text-[#4B5563]">
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Email</th>
                <th className="px-5 py-2.5">Role</th>
                <th className="px-5 py-2.5">Plan</th>
                <th className="px-5 py-2.5">Joined</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_signups.map((u) => (
                <tr key={u.id} className="border-b border-[#F3F4F6]" data-testid={`signup-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium">{u.name || "—"}{u.company ? <span className="text-[#9CA3AF]"> · {u.company}</span> : null}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`font-mono2 text-[11px] uppercase border px-2 py-0.5 ${u.role === "employer" ? "border-[#002FA7] text-[#002FA7]" : "border-[#0A0A0A] text-[#0A0A0A]"}`}>{u.role}</span>
                  </td>
                  <td className="px-5 py-3">
                    {u.plan ? (
                      <span className="font-mono2 text-[11px] uppercase text-[#7C3AED] inline-flex items-center gap-1"><Crown size={11} />{u.plan_name || u.plan}</span>
                    ) : (
                      <span className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">Free</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono2 text-xs text-[#4B5563]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
